import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Info, Moon, Sun, X, FileText, Loader2, Image as ImageIcon } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import ToolsNav from "../components/ToolsNav";
import { AdContainer, ResponsiveAd, StickyMobileAd } from "../components/AdPlacement";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/legacy/build/pdf.worker.min.mjs`;

function PdfToSvg() {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [svgImages, setSvgImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "PDF to SVG - Convert PDF to Vector Graphics - KayTool";
  }, []);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setSvgImages([]);
    } else {
      toast.error("Please select a valid PDF file");
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      setFile(droppedFile);
      setSvgImages([]);
    } else {
      toast.error("Please drop a valid PDF file");
    }
  };

  const convertToSvg = async () => {
    if (!file) return;
    
    setIsProcessing(true);
    setSvgImages([]);
    
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const svgPages = [];
      
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 1.5 });
        
        // Create canvas and render
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        
        await page.render({ canvasContext: context, viewport }).promise;
        
        // Convert to SVG by embedding the image
        const dataUrl = canvas.toDataURL('image/png');
        const svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" 
     width="${viewport.width}" height="${viewport.height}" viewBox="0 0 ${viewport.width} ${viewport.height}">
  <image width="${viewport.width}" height="${viewport.height}" xlink:href="${dataUrl}"/>
</svg>`;
        
        svgPages.push({
          pageNum: i,
          svgContent,
          width: viewport.width,
          height: viewport.height,
          preview: dataUrl
        });
      }
      
      setSvgImages(svgPages);
      toast.success(`Converted ${svgPages.length} pages to SVG`);
    } catch (error) {
      console.error("Conversion error:", error);
      toast.error("Failed to convert PDF to SVG");
    }
    
    setIsProcessing(false);
  };

  const downloadSvg = (svg) => {
    const blob = new Blob([svg.svgContent], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${file.name.replace('.pdf', '')}_page_${svg.pageNum}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadAll = async () => {
    if (svgImages.length === 0) return;
    
    if (svgImages.length === 1) {
      downloadSvg(svgImages[0]);
      return;
    }
    
    const zip = new JSZip();
    const baseName = file.name.replace('.pdf', '');
    
    for (const svg of svgImages) {
      zip.file(`${baseName}_page_${svg.pageNum}.svg`, svg.svgContent);
    }
    
    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, `${baseName}_svg_images.zip`);
    toast.success("ZIP file downloaded!");
  };

  const clearFile = () => {
    setFile(null);
    setSvgImages([]);
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-fuchsia-50 to-pink-50'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-500 to-pink-600 flex items-center justify-center">
              <ImageIcon className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
          </a>
          <div className="flex items-center gap-4">
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
              <Info className="w-3 h-3" /> 100% Local
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-fuchsia-500 to-pink-600 text-white rounded-full mb-4">
            <ImageIcon className="w-5 h-5" />
            <span className="font-medium">PDF to SVG</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Convert PDF to SVG
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Convert PDF pages to scalable vector graphics (SVG) format.
          </p>
        </div>

        {!file ? (
          <Card 
            className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 max-w-2xl mx-auto ${
              isDragging ? 'border-fuchsia-500 bg-fuchsia-50 dark:bg-fuchsia-900/20' : 'border-slate-300 dark:border-slate-600'
            }`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            <CardContent className="p-12 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
              <div 
                className="cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-16 h-16 mx-auto mb-4 text-fuchsia-500" />
                <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                  Drop PDF or click to upload
                </h3>
                <p className="text-slate-500 dark:text-slate-400">
                  Convert PDF pages to SVG vector graphics
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="max-w-6xl mx-auto space-y-6">
            <Card className="dark:bg-slate-800">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <FileText className="w-8 h-8 text-fuchsia-500" />
                  <div>
                    <h3 className="font-semibold dark:text-white">{file.name}</h3>
                    <p className="text-sm text-slate-500">
                      {svgImages.length > 0 ? `${svgImages.length} pages converted` : 'Ready to convert'}
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={clearFile}>
                  <X className="w-4 h-4 mr-1" /> Clear
                </Button>
              </CardContent>
            </Card>

            {svgImages.length === 0 && !isProcessing && (
              <div className="text-center">
                <Button 
                  onClick={convertToSvg}
                  size="lg"
                  className="bg-gradient-to-r from-fuchsia-500 to-pink-600"
                >
                  <ImageIcon className="w-5 h-5 mr-2" />
                  Convert to SVG
                </Button>
              </div>
            )}

            {isProcessing && (
              <div className="text-center py-12">
                <Loader2 className="w-12 h-12 mx-auto animate-spin text-fuchsia-500" />
                <p className="mt-4 text-slate-600 dark:text-slate-300">Converting pages to SVG...</p>
              </div>
            )}

            {svgImages.length > 0 && (
              <>
                <div className="flex justify-between items-center">
                  <h3 className="font-semibold dark:text-white">{svgImages.length} SVG Files</h3>
                  <Button onClick={downloadAll} className="bg-gradient-to-r from-fuchsia-500 to-pink-600">
                    <Download className="w-4 h-4 mr-2" />
                    Download All (ZIP)
                  </Button>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {svgImages.map((svg) => (
                    <Card key={svg.pageNum} className="overflow-hidden dark:bg-slate-800">
                      <div className="bg-slate-100 dark:bg-slate-700 p-2">
                        <img
                          src={svg.preview}
                          alt={`Page ${svg.pageNum}`}
                          className="w-full h-auto rounded"
                        />
                      </div>
                      <div className="p-2 flex justify-between items-center">
                        <Badge variant="secondary">Page {svg.pageNum}</Badge>
                        <Button size="sm" variant="ghost" onClick={() => downloadSvg(svg)}>
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

export default PdfToSvg;
